<script>
import { mapActions, mapGetters } from "vuex";

export default {
  data: () => ({
    isLoading: false,
    isAddModal: false,
    isDeleteModal: false,
    deleteTarget: null,
    editingScience: null,
    form: {
      id: null,
      code: "",
      name: "",
      questionLimit: 20,
      active: true,
    },
  }),

  computed: {
    ...mapGetters("admin-exam-questions", ["GET_SCIENCES"]),
    sciences: (vm) => vm.GET_SCIENCES,
  },

  created() {
    this.loadSciences();
  },

  methods: {
    ...mapActions("admin-exam-questions", ["INIT_SCIENCES", "SAVE_SCIENCE", "TOGGLE_SCIENCE", "DELETE_SCIENCE"]),

    async loadSciences() {
      this.isLoading = true;
      await this.INIT_SCIENCES();
      this.isLoading = false;
    },

    onAdd() {
      this.editingScience = null;
      this.form = { id: null, code: "", name: "", questionLimit: 20, active: true };
      this.isAddModal = true;
    },

    onEdit(item) {
      this.editingScience = item;
      this.form = {
        id: item.id,
        code: item.code || "",
        name: item.name || "",
        questionLimit: item.questionLimit || 20,
        active: item.active,
      };
      this.isAddModal = true;
    },

    async onSave() {
      if (!this.form.name || !this.form.code || !this.form.questionLimit || this.form.questionLimit < 1) return;
      const ok = await this.SAVE_SCIENCE(this.form);
      if (ok) {
        this.isAddModal = false;
        this.$root.$emit("snackbar", { text: this.$t("Saved"), color: "success" });
      } else {
        this.$root.$emit("snackbar", { text: this.$t("Error"), color: "error" });
      }
    },

    async onToggleActive(item) {
      await this.TOGGLE_SCIENCE({ id: item.id, active: !item.active });
    },

    onDelete(item) {
      this.deleteTarget = item;
      this.isDeleteModal = true;
    },

    async confirmDelete() {
      if (!this.deleteTarget) return;
      const ok = await this.DELETE_SCIENCE(this.deleteTarget.id);
      this.isDeleteModal = false;
      this.deleteTarget = null;
      if (ok) {
        this.$root.$emit("snackbar", { text: this.$t("Deleted"), color: "success" });
      } else {
        this.$root.$emit("snackbar", { text: this.$t("Error"), color: "error" });
      }
    },
  },
};
</script>

<template>
  <v-container fluid class="es-page">
    <div class="es-card">
      <div class="es-header">
        <div>
          <h2 class="es-title">{{ $t('exam_sciences') }}</h2>
          <p class="es-subtitle">{{ sciences.length }} {{ $t('fan') }}</p>
        </div>
        <button class="es-add-btn" @click="onAdd">
          <v-icon size="20" color="#fff">mdi-plus</v-icon>
          {{ $t('Add') }}
        </button>
      </div>

      <div v-if="isLoading" class="es-loading">
        <v-progress-circular indeterminate size="28" width="2.5" color="#00274c" />
      </div>

      <div v-else class="es-grid">
        <div
          v-for="(item, index) in sciences"
          :key="item.id"
          class="es-item"
          :class="{ 'es-item--inactive': !item.active }"
        >
          <div class="es-item__left">
            <span class="es-item__num">{{ index + 1 }}</span>
            <div class="es-item__info">
              <span class="es-item__name">{{ item.name }}</span>
              <span class="es-item__meta">
                {{ $t('Code') }}: <strong>{{ item.code }}</strong>
                &middot;
                {{ $t('Question limit') }}: <strong>{{ item.questionLimit }}</strong>
              </span>
            </div>
          </div>
          <div class="es-item__actions">
            <button class="es-icon-btn es-icon-btn--edit" @click="onEdit(item)" :title="$t('Edit')">
              <v-icon size="16" color="#fff">mdi-pencil-outline</v-icon>
            </button>
            <button class="es-icon-btn es-icon-btn--delete" @click="onDelete(item)" :title="$t('Delete')">
              <v-icon size="16" color="#fff">mdi-trash-can-outline</v-icon>
            </button>
            <v-switch
              :input-value="item.active"
              @change="onToggleActive(item)"
              hide-details dense inset
              class="es-switch"
            />
          </div>
        </div>

        <div v-if="!sciences.length" class="es-empty">
          <v-icon size="40" color="#cbd5e1">mdi-book-open-blank-variant</v-icon>
          <span>{{ $t("No data") }}</span>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <v-dialog v-model="isDeleteModal" max-width="400">
      <v-card class="es-dialog">
        <div class="es-dialog__header">
          <h3>{{ $t('Delete') }}?</h3>
          <button class="es-close" @click="isDeleteModal = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="es-dialog__body">
          <p><strong>{{ deleteTarget?.name }}</strong> ({{ deleteTarget?.code }})</p>
        </div>
        <div class="es-dialog__footer">
          <button class="es-btn es-btn--ghost" @click="isDeleteModal = false">
            {{ $t("Cancel") }}
          </button>
          <button class="es-btn es-btn--danger" @click="confirmDelete">
            <v-icon size="15" class="mr-1">mdi-trash-can-outline</v-icon>
            {{ $t("Delete") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Add/Edit -->
    <v-dialog v-model="isAddModal" max-width="460" persistent>
      <v-card class="es-dialog">
        <div class="es-dialog__header">
          <h3>{{ editingScience ? $t('Edit') : $t('Add') }} — {{ $t('Fan') }}</h3>
          <button class="es-close" @click="isAddModal = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="es-dialog__body">
          <div class="es-form-grid">
            <v-text-field v-model="form.code" :label="$t('Code')" outlined dense hide-details />
            <v-text-field v-model.number="form.questionLimit" :label="$t('Question limit')" outlined dense hide-details type="number" min="1" />
          </div>
          <v-text-field v-model="form.name" :label="$t('Name')" outlined dense hide-details autofocus class="mt-4" />
        </div>
        <div class="es-dialog__footer">
          <button class="es-btn es-btn--ghost" @click="isAddModal = false">{{ $t("Cancel") }}</button>
          <button
            class="es-btn es-btn--primary"
            @click="onSave"
            :disabled="!form.name || !form.code || !form.questionLimit || form.questionLimit < 1"
          >
            <v-icon size="15" class="mr-1">mdi-check</v-icon>
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style lang="scss" scoped>
.es-page { max-width: 100%; padding-top: 8px; }

.es-card {
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 16px;
  overflow: hidden;
}

.es-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f1f3;
}

.es-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}

.es-subtitle {
  font-size: 12px;
  color: #94a3b8;
  margin: 2px 0 0;
  font-weight: 500;
}

.es-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #00274c;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background .15s;
  font-family: inherit;
  &:hover { background: #003566; }
  &:active { transform: scale(.98); }
}

.es-loading {
  display: flex;
  justify-content: center;
  padding: 56px;
}

.es-grid {
  display: flex;
  flex-direction: column;
}

.es-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-bottom: 1px solid #f0f1f3;
  transition: background .1s;

  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }

  &--inactive {
    opacity: .45;
    &:hover { opacity: .6; }
  }
}

.es-item__left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.es-item__num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  background: #f1f5f9;
  border-radius: 7px;
  font-variant-numeric: tabular-nums;
}

.es-item__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.es-item__name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.es-item__meta {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 1px;
  strong { color: #64748b; font-weight: 600; }
}

.es-item__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 16px;
}

.es-icon-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all .15s;

  &--edit {
    background: #7c3aed;
    &:hover { background: #6d28d9; }
  }
  &--delete {
    background: #ef4444;
    &:hover { background: #dc2626; }
  }
  &:active { transform: scale(.93); }
}

.es-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.es-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 56px 24px;
  color: #94a3b8;
  font-size: 13px;
}

/* Dialog */
.es-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}
.es-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  h3 { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
}
.es-close {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; border: none; background: none;
  cursor: pointer; color: #94a3b8;
  &:hover { background: #f1f5f9; color: #475569; }
}
.es-dialog__body {
  padding: 4px 24px 20px;
  p { margin: 0; font-size: 14px; color: #64748b; }
}
.es-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.es-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 20px;
  border-top: 1px solid #f0f1f3;
}
.es-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all .12s;
  font-family: inherit;

  .v-icon { color: currentColor; }

  &--ghost {
    background: #fff;
    color: #475569;
    border: 1px solid #d0d5dd;
    &:hover { background: #f8fafc; }
  }
  &--primary {
    background: #00274c;
    color: #fff;
    &:hover { background: #003566; }
    &:disabled { opacity: .4; cursor: not-allowed; }
  }
  &--danger {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    &:hover { background: #fee2e2; }
  }
}

/* Dark */
.theme--dark {
  .es-card { background: #1a1b23; border-color: #2a2c3a; }
  .es-header { border-color: #2a2c3a; }
  .es-title { color: #e4e5eb; }
  .es-subtitle { color: #606275; }
  .es-add-btn { background: #ffd451; color: #1a1500; &:hover { background: #f5c542; } }
  .es-item { border-color: #2a2c3a; &:hover { background: #22232e; } }
  .es-item__num { background: #2a2c3a; color: #8b8d9e; }
  .es-item__name { color: #e4e5eb; }
  .es-item__meta { color: #606275; strong { color: #8b8d9e; } }
  .es-empty { color: #606275; }
  .es-dialog { background: #1a1b23 !important; }
  .es-dialog__header h3 { color: #e4e5eb; }
  .es-dialog__footer { border-color: #2a2c3a; }
  .es-close { color: #606275; &:hover { background: #2a2c3a; color: #d5d6de; } }
  .es-btn--ghost { background: #22232e; color: #d5d6de; border-color: #33354a; &:hover { background: #2a2c3a; } }
  .es-btn--primary { background: #ffd451; color: #1a1500; }
}
</style>
